// StockPage.jsx
import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Colors } from "../../../assets/Colors";
import { fetchGet } from "../../../fetchers";
import { useQuery } from "@tanstack/react-query";
import { Card } from "@material-tailwind/react";
import { HrdInputGenerator } from "../../../components/form/HrdInputGenerator";
import { toast } from "react-toastify";
import { maxPageGlobal } from "../../../assets/config";
import TableStock, { saldoAkhirTampil } from "../../../components/tables/TableStock";
import debounce from "lodash/debounce";
import { useForm } from "../../../components/isRequired";
import Skeltons from "../../../components/loading/Skeltons";
import { BiSearch } from "react-icons/bi";
import { BsFileEarmarkExcelFill, BsFileEarmarkPdfFill } from "react-icons/bs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// scope="all"    -> menu Stock: menampilkan seluruh Bagian (perilaku lama, tak berubah)
// scope="bagian" -> menu Stok per Bagian: hanya barang yang dikelola Bagian pengguna.
//                   Pembatasannya dilakukan SERVER lewat endpoint terpisah; komponen ini
//                   hanya memilih endpoint & label, jadi tak ada rahasia yang bergantung
//                   pada kode di browser.
const StockPage = ({ scope = "all" }) => {
  const perBagian = scope === "bagian";
  const apiBase = perBagian ? "Procurement/GoodsStockBagian" : "Procurement/GoodsStock";
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;
  const currentYear = currentDate.getFullYear();

  // State management - Mengikuti pola MonitoringPattyCashHO
  const [searchInput, setSearchInput] = useState(""); // Untuk input field (langsung update)
  const [searchQuery, setSearchQuery] = useState(""); // Untuk query ke API (debounced)
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(maxPageGlobal);
  const [showAll, setShowAll] = useState(false);
  const [bisnisUnit, setBisnisUnit] = useState("U0002");
  const [jenisBarang, setJenisBarang] = useState(""); // "" = semua, "Logistik", "Material"
  const [periode, setPeriode] = useState({
    bulan: currentMonth.toString(),
    tahun: currentYear.toString()
  });
  const [showModal, setShowModal] = useState(false);
  const [detail, setDetail] = useState({});
  const [isExporting, setIsExporting] = useState(false);
  const [isLibraryReady, setIsLibraryReady] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  // Form handling untuk filter
  const initialState = {
    Bulan: currentDate,
    BisnisUnitID: "U0002",
  };

  const validations = [];

  const { values, errors, touched, customHandler } = useForm(
    initialState,
    validations
  );

  // Load Excel library dynamically
  useEffect(() => {
    if (window.XLSX) {
      setIsLibraryReady(true);
      return;
    }

    const loadLibrary = async () => {
      try {
        const script = document.createElement('script');
        script.src = 'https://cdn.sheetjs.com/xlsx-0.20.2/package/dist/xlsx.full.min.js';
        script.async = true;
        
        script.onload = () => {
          console.log('Excel library loaded successfully');
          setIsLibraryReady(true);
        };
        
        script.onerror = () => {
          const altScript = document.createElement('script');
          altScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
          altScript.async = true;
          
          altScript.onload = () => {
            console.log('Excel library loaded from alternative CDN');
            setIsLibraryReady(true);
          };
          
          altScript.onerror = () => {
            console.error('Failed to load Excel library');
            toast.error('Gagal memuat library Excel. Silakan refresh halaman.', {
              position: "top-center",
              autoClose: 5000,
            });
          };
          
          document.body.appendChild(altScript);
        };
        
        document.body.appendChild(script);
      } catch (error) {
        console.error('Error loading library:', error);
      }
    };

    loadLibrary();
  }, []);

  // Debounced search - Mengikuti pola MonitoringPattyCashHO
  const debouncedSetSearchQuery = useMemo(
    () =>
      debounce((value) => {
        console.log("Debounced search value:", value);
        setSearchQuery(value);
        setPage(1);
        setIsSearching(false);
      }, 600),
    []
  );

  // Cleanup debounce
  useEffect(() => {
    return () => debouncedSetSearchQuery.cancel();
  }, [debouncedSetSearchQuery]);

  // Handler untuk search input - Mengikuti pola MonitoringPattyCashHO
  const handleSearchChange = useCallback(
    (e) => {
      // Jika dari event input
      if (e && e.target) {
        const value = e.target.value;
        setSearchInput(value);

        debouncedSetSearchQuery.cancel();

        if (value.trim() === "") {
          setSearchQuery("");
          setPage(1);
          setIsSearching(false);
        } else {
          setIsSearching(true);
          debouncedSetSearchQuery(value);
        }
      } 
      // Jika dari props string (dari TableStock)
      else if (typeof e === 'string') {
        const value = e;
        setSearchInput(value);

        debouncedSetSearchQuery.cancel();

        if (value.trim() === "") {
          setSearchQuery("");
          setPage(1);
          setIsSearching(false);
        } else {
          setIsSearching(true);
          debouncedSetSearchQuery(value);
        }
      }
    },
    [debouncedSetSearchQuery]
  );

  // Handler untuk clear search
  const handleClearSearch = useCallback(() => {
    debouncedSetSearchQuery.cancel();
    setSearchInput("");
    setSearchQuery("");
    setPage(1);
    setIsSearching(false);

    toast.info("Pencarian dibersihkan", {
      position: "top-center",
      autoClose: 800,
    });
  }, [debouncedSetSearchQuery]);

  // Update bisnis unit ketika nilai form berubah
  useEffect(() => {
    if (values.BisnisUnitID) {
      setBisnisUnit(values.BisnisUnitID);
      setPage(1);
      setSearchInput("");
      setSearchQuery("");
      debouncedSetSearchQuery.cancel();
    }
  }, [values.BisnisUnitID]);

  // Update periode ketika nilai form berubah
  useEffect(() => {
    if (values.Bulan) {
      const selectedDate = new Date(values.Bulan);
      const bulan = (selectedDate.getMonth() + 1).toString();
      const tahun = selectedDate.getFullYear().toString();
      setPeriode({ bulan, tahun });
      setPage(1);
      setSearchInput("");
      setSearchQuery("");
      debouncedSetSearchQuery.cancel();
    }
  }, [values.Bulan]);

  // Fetch data dengan React Query
  const {
    data: stockData,
    isLoading,
    isFetching,
    refetch,
    error
  } = useQuery({
    queryKey: ["goodsStock", scope, showAll, bisnisUnit, periode.bulan, periode.tahun, page, limit, searchQuery, jenisBarang],
    queryFn: async () => {
      let endpoint = "";
      
      if (showAll) {
        endpoint = `${apiBase}/BuOnly/${bisnisUnit}`;
      } else {
        endpoint = `${apiBase}/${bisnisUnit}/${periode.bulan}/${periode.tahun}`;
      }

      const params = new URLSearchParams();
      if (searchQuery && searchQuery.trim() !== "") {
        params.append("search", searchQuery.trim());
      }
      if (jenisBarang) {
        params.append("jenis", jenisBarang);
      }
      params.append("page", page.toString());
      params.append("limit", limit.toString());

      const url = `${endpoint}?${params.toString()}`;
      console.log("Fetching:", url);

      const response = await fetchGet(url);
      return response;
    },
    keepPreviousData: true,
    staleTime: 30000,
  });

  // Function to fetch ALL data for export
  const fetchAllDataForExport = useCallback(async () => {
    try {
      let endpoint = "";
      if (showAll) {
        endpoint = `${apiBase}/BuOnly/${bisnisUnit}`;
      } else {
        endpoint = `${apiBase}/${bisnisUnit}/${periode.bulan}/${periode.tahun}`;
      }

      const params = new URLSearchParams();
      if (searchQuery && searchQuery.trim() !== "") {
        params.append("search", searchQuery.trim());
      }
      if (jenisBarang) {
        params.append("jenis", jenisBarang);
      }
      params.append("page", "1");
      params.append("limit", "1");

      const firstPage = await fetchGet(`${endpoint}?${params.toString()}`);
      const totalCount = firstPage.count || 0;
      
      if (totalCount === 0) {
        return [];
      }

      const chunkSize = 1000;
      const totalChunks = Math.ceil(totalCount / chunkSize);
      let allData = [];

      const toastId = toast.loading(`Mengumpulkan data 0/${totalCount}...`, {
        position: "top-center",
      });

      for (let i = 0; i < totalChunks; i++) {
        const chunkParams = new URLSearchParams();
        if (searchQuery && searchQuery.trim() !== "") {
          chunkParams.append("search", searchQuery.trim());
        }
        if (jenisBarang) {
          chunkParams.append("jenis", jenisBarang);
        }
        chunkParams.append("page", (i + 1).toString());
        chunkParams.append("limit", chunkSize.toString());

        const response = await fetchGet(`${endpoint}?${chunkParams.toString()}`);
        allData = [...allData, ...(response.data || [])];
        
        toast.update(toastId, {
          render: `Mengumpulkan data ${Math.min(allData.length, totalCount)}/${totalCount}...`,
        });
      }

      toast.dismiss(toastId);
      return allData;
    } catch (error) {
      console.error("Error fetching all data:", error);
      toast.dismiss();
      throw error;
    }
  }, [showAll, bisnisUnit, periode, searchQuery, jenisBarang]);

  // Export to Excel function
  const handleExportExcel = useCallback(async () => {
    if (!isLibraryReady) {
      toast.error("Library Excel sedang dimuat. Silakan tunggu beberapa saat.", {
        position: "top-center",
        autoClose: 3000,
      });
      return;
    }

    if (typeof window.XLSX === "undefined") {
      toast.error("Library Excel tidak tersedia. Silakan refresh halaman.", {
        position: "top-center",
        autoClose: 3000,
      });
      return;
    }

    try {
      setIsExporting(true);

      const filterInfo = [];
      if (jenisBarang) {
        filterInfo.push(`Jenis: ${jenisBarang}`);
      }
      if (searchQuery && searchQuery.trim() !== "") {
        filterInfo.push(`Search: "${searchQuery}"`);
      }
      if (showAll) {
        filterInfo.push("Semua Periode");
      } else {
        filterInfo.push(`Periode: Bulan ${periode.bulan} Tahun ${periode.tahun}`);
      }

      toast.info(`Mengexport data dengan filter: ${filterInfo.join(', ')}`, {
        position: "top-center",
        autoClose: 2000,
      });

      const allData = await fetchAllDataForExport();

      if (!allData || allData.length === 0) {
        toast.warn("Tidak ada data untuk di-export", {
          position: "top-center",
          autoClose: 2000,
        });
        return;
      }

      toast.info(`Menyiapkan ${allData.length} data untuk export...`, {
        position: "top-center",
        autoClose: 2000,
      });

      const excelData = allData.map((item, index) => {
        const tersediaQty = (parseFloat(item.SaldoAkhir) || 0) - (parseFloat(item.MinQty) || 0);
        const tersediaNominal = (parseFloat(item.SaldoAwalNominal) || 0) + (parseFloat(item.PenerimaanNominal) || 0);
        const stockStatus = (parseFloat(item.SaldoAkhir) - parseFloat(item.MinQty)) <= 0 ? "Segera PO" : "Stock Tersedia";

        return {
          No: index + 1,
          "Barang ID": item.BarangID || "",
          "Kode Stock Opname": item.KodeStockOpname || "",
          "Nama Barang": item.NamaBarang || "",
          "Satuan": item.NamaSatuan || "",
          "Min Qty": item.MinQty || 0,
          "Saldo Awal Qty": item.SaldoAwal || 0,
          "Saldo Awal Nominal": item.SaldoAwalNominal || 0,
          "Penerimaan Qty": item.Penerimaan || 0,
          "Penerimaan Nominal": item.PenerimaanNominal || 0,
          "Tersedia Qty": tersediaQty,
          "Tersedia Nominal": tersediaNominal,
          "Pengeluaran Qty": (item.Pengeluaraan || 0),
          "Pengeluaran Nominal": (item.PengeluaraanNominal || 0),
          "Transit In": item.QtyBelumDiterima || 0,
          "Transit Out": item.QtyTransitOut || 0,
          "Saldo Akhir Qty": saldoAkhirTampil(item),
          "Saldo Akhir Nominal": item.SaldoAkhirNominal || 0,
          "Status": stockStatus
        };
      });

      const XLSX = window.XLSX;
      const ws = XLSX.utils.json_to_sheet(excelData);

      const colWidths = [
        { wch: 5 },   // No
        { wch: 15 },  // Barang ID
        { wch: 18 },  // Kode Stock Opname
        { wch: 30 },  // Nama Barang
        { wch: 10 },  // Satuan
        { wch: 10 },  // Min Qty
        { wch: 15 },  // Saldo Awal Qty
        { wch: 15 },  // Saldo Awal Nominal
        { wch: 15 },  // Penerimaan Qty
        { wch: 15 },  // Penerimaan Nominal
        { wch: 15 },  // Tersedia Qty
        { wch: 15 },  // Tersedia Nominal
        { wch: 15 },  // Pengeluaran Qty
        { wch: 15 },  // Pengeluaran Nominal
        { wch: 15 },  // Transit In
        { wch: 15 },  // Transit Out
        { wch: 15 },  // Saldo Akhir Qty
        { wch: 15 },  // Saldo Akhir Nominal
        { wch: 15 },  // Status
      ];
      ws["!cols"] = colWidths;

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Stock Barang");

      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, "-");
      const filename = `Stock_Barang_${timestamp}.xlsx`;

      XLSX.writeFile(wb, filename);

      toast.success(`Berhasil export ${allData.length} data stock!`, {
        position: "top-center",
        autoClose: 3000,
      });
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Gagal melakukan export data: " + (error.message || "Unknown error"), {
        position: "top-center",
        autoClose: 3000,
      });
    } finally {
      setIsExporting(false);
    }
  }, [fetchAllDataForExport, isLibraryReady, searchQuery, showAll, periode, jenisBarang]);

  // Handler untuk show all
  const handleShowAll = useCallback((e) => {
    const checked = e.target.checked;
    setShowAll(checked);
    setPage(1);
    setSearchInput("");
    setSearchQuery("");
    setIsSearching(false);
    debouncedSetSearchQuery.cancel();
    
    if (checked) {
      toast.info("Menampilkan semua periode", { 
        position: "top-center",
        autoClose: 1000 
      });
    }
  }, [debouncedSetSearchQuery]);

  // Handler untuk pagination
  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Handler untuk change limit per page
  const handleLimitChange = useCallback((newLimit) => {
    setLimit(newLimit === -1 ? 999999 : newLimit);
    setPage(1);
  }, []);

  const handleExportPDF = useCallback(async () => {
    setIsExporting(true);
    try {
      const allData = await fetchAllDataForExport();
      if (!allData || allData.length === 0) {
        toast.warn("Tidak ada data untuk di-export", { position: "top-center", autoClose: 2000 });
        return;
      }
      const doc = new jsPDF({ orientation: "landscape" });
      const judulDasar = perBagian ? "Stock Barang (Bagian Saya)" : "Stock Barang";
      const title = showAll
        ? `${judulDasar} — Semua Periode | BU: ${bisnisUnit}`
        : `${judulDasar} — Bulan ${periode.bulan} Tahun ${periode.tahun} | BU: ${bisnisUnit}`;
      doc.setFontSize(13);
      doc.text(title, 14, 15);
      autoTable(doc, {
        startY: 22,
        styles: { fontSize: 6.5 },
        headStyles: { fillColor: [30, 58, 95] },
        head: [["No", "Barang ID", "Kode Stock", "Nama Barang", "Satuan", "Min Qty",
          "Saldo Awal", "Saldo Awal Nom.", "Penerimaan", "Penerimaan Nom.",
          "Tersedia", "Tersedia Nom.", "Pengeluaran", "Pengeluaran Nom.",
          "Transit In", "Transit Out", "Saldo Akhir", "Saldo Akhir Nom.", "Status"]],
        body: allData.map((item, index) => {
          const tersedia = (parseFloat(item.SaldoAkhir) || 0) - (parseFloat(item.MinQty) || 0);
          const tersediaNom = (parseFloat(item.SaldoAwalNominal) || 0) + (parseFloat(item.PenerimaanNominal) || 0);
          const status = tersedia <= 0 ? "Segera PO" : "Stock Tersedia";
          return [
            index + 1,
            item.BarangID || "",
            item.KodeStockOpname || "",
            item.NamaBarang || "",
            item.NamaSatuan || "",
            item.MinQty || 0,
            item.SaldoAwal || 0,
            item.SaldoAwalNominal || 0,
            item.Penerimaan || 0,
            item.PenerimaanNominal || 0,
            tersedia,
            tersediaNom,
            item.Pengeluaraan || 0,
            item.PengeluaraanNominal || 0,
            item.QtyBelumDiterima || 0,
            item.QtyTransitOut || 0,
            saldoAkhirTampil(item),
            item.SaldoAkhirNominal || 0,
            status,
          ];
        }),
      });
      const timestamp = new Date().toISOString().slice(0, 10);
      doc.save(`Stock_Barang_${timestamp}.pdf`);
      toast.success(`Berhasil export ${allData.length} data ke PDF!`, { position: "top-center", autoClose: 3000 });
    } catch (error) {
      toast.error("Gagal export PDF: " + (error.message || ""), { position: "top-center", autoClose: 3000 });
    } finally {
      setIsExporting(false);
    }
  }, [fetchAllDataForExport, showAll, bisnisUnit, periode, jenisBarang]);

  const ModalOpener = useCallback((detail) => {
    setShowModal(true);
    setDetail(detail);
  }, []);

  // Data preparation
  const tableData = stockData?.data || [];
  const totalRows = stockData?.count || 0;
  const isLoadingData = isLoading || isFetching;

  // Render export button
  const renderExportButton = () => {
    if (!isLibraryReady) {
      return (
        <button
          disabled={true}
          className="flex items-center gap-1 bg-white text-gray-400 font-semibold py-2 px-4 border border-gray-300 rounded shadow text-sm cursor-not-allowed opacity-60"
        >
          <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Memuat Library...
        </button>
      );
    }

    return (
      <button
        onClick={handleExportExcel}
        disabled={isExporting || isLoadingData || totalRows === 0}
        className="flex items-center gap-1 bg-white hover:bg-gray-100 text-gray-800 font-semibold py-2 px-4 border border-gray-400 rounded shadow text-sm disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isExporting ? (
          <>
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
            Mengexport...
          </>
        ) : (
          <>
            <BsFileEarmarkExcelFill />
            Export Excel
          </>
        )}
      </button>
    );
  };

  // Tampilkan error jika ada
  if (error) {
    return (
      <div className="md:ml-64">
        <div className="px-3 md:px-8 h-auto mt-32">
          <div className="container mx-auto max-w-full">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              Error: {error.message}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="md:ml-64">
      <div
        style={{ background: Colors.primary }}
        className="pt-14 pb-16 px-3 md:px-8 h-auto"
      >
        <div className="container mx-auto max-w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4"></div>
        </div>
      </div>

      <div className="px-3 md:px-8 h-auto -mt-32">
        <div className="container mx-auto max-w-full">
          <div className="grid grid-cols-1 px-4">
            <Card>
              <div className="p-6">
                {/* Filter Section */}
                <div className="mb-6">
                  <div className="flex flex-wrap gap-4 items-end">
                    {/* Bisnis Unit */}
                    <div className="w-full md:w-64">
                      <HrdInputGenerator
                        type="BisnisUnit"
                        name="BisnisUnitID"
                        title="Bisnis Unit"
                        value={values}
                        required={true}
                        onChange={customHandler}
                        touched={touched}
                        errors={errors}
                        size="full"
                      />
                    </div>

                    {/* Periode */}
                    <div className="w-full md:w-64">
                      <HrdInputGenerator
                        type="bulan"
                        name="Bulan"
                        title="Periode"
                        value={values}
                        required={true}
                        onChange={customHandler}
                        touched={touched}
                        errors={errors}
                        size="full"
                      />
                    </div>

                    {/* Jenis Barang */}
                    <div className="w-full md:w-48 px-3 mb-6">
                      <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                        Jenis Barang
                      </label>
                      <select
                        value={jenisBarang}
                        onChange={(e) => {
                          setJenisBarang(e.target.value);
                          setPage(1);
                        }}
                        className="block w-full h-10 bg-white border border-gray-300 rounded px-3 text-sm text-gray-700 focus:outline-none focus:border-blue-500"
                      >
                        <option value="">Semua</option>
                        <option value="Logistik">Logistik</option>
                        <option value="Material">Material</option>
                      </select>
                    </div>

                    {/* Show All Checkbox */}
                    <div className="px-3 mb-6">
                      <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">&nbsp;</label>
                      <div className="flex items-center py-2">
                        <input
                          type="checkbox"
                          id="showAll"
                          onChange={handleShowAll}
                          checked={showAll}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <label htmlFor="showAll" className="ml-2 text-sm text-gray-700 whitespace-nowrap">
                          Show All Periode
                        </label>
                      </div>
                    </div>

                    {/* Export Excel Button */}
                    <div className="px-3 mb-6">
                      <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">&nbsp;</label>
                      {renderExportButton()}
                    </div>

                    {/* Export PDF Button */}
                    <div className="px-3 mb-6">
                      <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">&nbsp;</label>
                      <button
                        onClick={handleExportPDF}
                        disabled={isExporting || isLoadingData || totalRows === 0}
                        className="flex items-center gap-1 bg-white hover:bg-gray-100 text-gray-800 font-semibold py-2 px-4 border border-gray-400 rounded shadow text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isExporting ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                            Mengexport...
                          </>
                        ) : (
                          <>
                            <BsFileEarmarkPdfFill />
                            Export PDF
                          </>
                        )}
                      </button>
                    </div>

                    {/* Info Loading */}
                    {isFetching && !isLoading && (
                      <div className="px-3 mb-6">
                        <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">&nbsp;</label>
                        <div className="flex items-center py-2">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                          <span className="ml-2 text-sm text-gray-500">Memuat...</span>
                        </div>
                      </div>
                    )}

                    {/* Info Total Data */}
                    {totalRows > 0 && (
                      <div className="px-3 mb-6">
                        <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">&nbsp;</label>
                        <div className="text-sm text-gray-600 py-2">
                          Total: <strong>{totalRows}</strong> data
                          {searchQuery && ` (hasil pencarian "${searchQuery}")`}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Info Filter yang Ditampilkan */}
                <div className="mb-4 px-4 py-2 bg-blue-50 border rounded-md">
                  <p className="text-sm text-blue-700">
                    {showAll ? (
                      <>Menampilkan <strong>SEMUA PERIODE</strong> untuk Bisnis Unit: <strong>{bisnisUnit}</strong></>
                    ) : (
                      <>Menampilkan periode: <strong>Bulan {periode.bulan} Tahun {periode.tahun}</strong> | Bisnis Unit: <strong>{bisnisUnit}</strong></>
                    )}
                    {searchQuery && (
                      <span className="ml-2">
                        | Pencarian: <strong>"{searchQuery}"</strong>
                      </span>
                    )}
                  </p>
                </div>

                {/* Search Bar - SATU-SATUNYA search bar di halaman ini */}
                <div className="mb-4">
                  <div className="relative w-full md:w-96">
                    <BiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Cari Barang ID, Nama Barang, Kode Stock..."
                      value={searchInput}
                      onChange={handleSearchChange}
                      className="pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full"
                    />
                    {searchInput && (
                      <button
                        onClick={handleClearSearch}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        title="Clear search"
                      >
                        ✕
                      </button>
                    )}

                    {isSearching && (
                      <div className="absolute right-8 top-1/2 transform -translate-y-1/2">
                        <div className="flex items-center space-x-1">
                          <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                          <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                          <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Status Pencarian */}
                  {searchInput !== searchQuery && searchInput && (
                    <p className="text-xs text-blue-500 mt-1 flex items-center">
                      <span className="animate-pulse mr-1">●</span>
                      Mencari "{searchInput}"...
                    </p>
                  )}

                  {searchQuery && !isSearching && (
                    <p className="text-xs text-green-600 mt-1 flex items-center">
                      <span className="mr-1">✓</span>
                      Menampilkan hasil untuk "{searchQuery}"
                    </p>
                  )}
                </div>

                {/* Table Component - CATATAN: TableStock memiliki search sendiri di dalamnya, 
                    tapi kita tetap kirim props untuk kompatibilitas */}
                {isLoading ? (
                  <div className="w-full">
                    <Skeltons />
                  </div>
                ) : (
                  <TableStock
                    data={tableData}
                    onDetail={ModalOpener}
                    isLoading={isLoadingData}
                    onPageChange={handlePageChange}
                    onPerRowsChange={handleLimitChange}
                    totalRows={totalRows}
                    currentPage={page}
                    perPage={limit}
                    search={searchInput}
                    onSearchChange={handleSearchChange}
                    showAll={showAll}
                    bisnisUnit={bisnisUnit}
                    periode={periode}
                  />
                )}
              </div>
            </Card>

            {/* Modal Detail */}
            {showModal && (
              <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                  <div className="mt-3">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Detail Stock</h3>
                    <div className="space-y-2">
                      <p className="text-sm text-gray-600">
                        <span className="font-semibold">Barang ID:</span> {detail.BarangID}
                      </p>
                      <p className="text-sm text-gray-600">
                        <span className="font-semibold">Nama Barang:</span> {detail.NamaBarang}
                      </p>
                      <p className="text-sm text-gray-600">
                        <span className="font-semibold">Satuan:</span> {detail.NamaSatuan}
                      </p>
                      <p className="text-sm text-gray-600">
                        <span className="font-semibold">Min Qty:</span> {detail.MinQty?.toLocaleString()}
                      </p>
                      <p className="text-sm text-gray-600">
                        <span className="font-semibold">Saldo Akhir:</span> {detail.SaldoAkhir?.toLocaleString()}
                      </p>
                      <p className="text-sm text-gray-600">
                        <span className="font-semibold">Saldo Akhir Nominal:</span> Rp {detail.SaldoAkhirNominal?.toLocaleString()}
                      </p>
                    </div>
                    <div className="mt-6 flex justify-end">
                      <button
                        onClick={() => setShowModal(false)}
                        className="flex items-center gap-1 bg-white hover:bg-gray-100 text-gray-800 font-semibold py-2 px-4 border border-gray-400 rounded shadow text-sm"
                      >
                        Tutup
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockPage;